import { useState, useEffect, useCallback } from "react";
import { getEntitySubtypes } from "../../services/api";
import { apiFetch } from "../../services/api";
import { entitySubtypesCache } from "../../services/localCache";
import { createDevConsole } from "../../utils/devLogger";
import { parseEntityIdArray } from "../../utils/entityIdArrays";

const console = createDevConsole(false);

export const useEntityForm = (
  editingEntity,
  entities = [],
  relationships = [],
  { defaultEntityType = "" } = {}
) => {
  const entityId = String(editingEntity?.id ?? "").trim();
  const isExistingEntity = Boolean(entityId);
  // Cache for subtypes by entity type - pre-loaded for all types
  const [subtypesCache, setSubtypesCache] = useState({});
  const [loadingAllSubtypes, setLoadingAllSubtypes] = useState(false);

  const [entityForm, setEntityForm] = useState({
    name: "",
    type: "", // Will be set dynamically from API
    description: "",
    status: "", // lifecycle/status
    custom_attributes: {}, // Custom attributes for entity type
    alias_names: "", // Comma or semicolon delimited alternate names
    programming_language: "",
    database_type: "",
    business_capability: "",
    business_unit: "",
    level: "",
    deployment_environment: "",
    user_count: "",
    subtype: "",
    // Hierarchical/sequential relationships for RelationshipManager
    parent: "",
    children: [],
    predecessors: [],
    successors: [],
    // Relationship arrays
    related_organizations: [],
    related_initiatives: [],
    related_business_capabilities: [],
    related_applications: [],
    related_it_components: [],
    related_business_actors: [],
    related_data_objects: [],
    related_business_contexts: [],
    related_providers: [],
    related_goals: [],
    related_business_processes: [],
    related_value_chains: [],
    provided_interfaces: [],
    consumed_interfaces: [],
    interface_provided_by_app: [],
    interface_exposed_by_app: [],
    interface_consumed_by_app: [],
    interface_affected_by_initiative: [],
    interface_affected_by_initiative_reverse: [],
    organization_owns_initiative_reverse: [],
    organization_owns_objective_reverse: [], // Organization outgoing relationships - Which objectives are owned by this organization?
    organization_related_business_contexts: [], // Organization outgoing relationships
    provider_supports_initiative_reverse: [],
    // Data object incoming relationships
    data_object_transferred_by_interface: [],
    data_object_affected_by_initiative: [],
    // Technology component incoming relationships
    technology_component_used_by_interface: [],
    technology_component_used_by_application: [],
    technology_component_offered_by_provider: [],
    it_provided_by_provider_reverse: [], // Alias for technology_component_offered_by_provider (registry key)
    technology_component_owned_by_organization: [],
    technology_component_affected_by_initiative: [],
    technology_component_implements_platform: [],
    related_successors: [],
    platform_supports_objective: [],
    // Business context relationships
    business_context_associated_with_business_capability: [], // Business context outgoing relationships
    // Business context incoming relationships (business-context is target)
    initiative_affects_business_context_reverse: [],
    application_supports_business_context_reverse: [],
    organization_owns_business_context_reverse: [],
    business_capability_linked_to_business_context_reverse: [],
    // Objective incoming relationships
    objective_supported_by_initiative: [],
    objective_supported_by_platform: [],
    // Application incoming relationships
    initiative_affects_application_reverse: [],
    platform_runs_application_reverse: [],
    // Business context incoming relationships
    initiative_affects_business_context_reverse: [],
    // Business capability incoming relationships
    initiative_improves_business_capability_reverse: [],
    objective_improves_business_capability_reverse: [],
    // Platform incoming relationships
    initiative_affects_platform_reverse: [],
    // Parent relationship arrays
    related_organization_parents: [],
    related_initiative_parents: [],
    related_businesscontext_parents: [],
    related_businesscapability_parents: [],
    related_applicationinterface_parents: [],
    // Initiative-specific fields removed: affected_*
  });

  const [availableSubtypes, setAvailableSubtypes] = useState([]);
  const [loadingSubtypes, setLoadingSubtypes] = useState(false);
  const [removedItems, setRemovedItems] = useState({});
  const [lastEditingEntitySignature, setLastEditingEntitySignature] =
    useState(null);

  // Set default entity type when creating a new entity
  useEffect(() => {
    if (isExistingEntity || entityForm.type) return;
    const preset = String(defaultEntityType || "").trim();
    if (preset) {
      setEntityForm((prev) => ({ ...prev, type: preset }));
      return;
    }
    const setDefaultEntityType = async () => {
      try {
        const { TYPE_PROFILE } = await import("../../utils/typeProfile");
        const types = Object.values(TYPE_PROFILE);
        if (types.length > 0) {
          setEntityForm((prev) => ({
            ...prev,
            type: types[0].value,
          }));
        }
      } catch (error) {
        console.error("Error loading entity types for default:", error);
        setEntityForm((prev) => ({
          ...prev,
          type: "application",
        }));
      }
    };
    void setDefaultEntityType();
  }, [isExistingEntity, defaultEntityType, entityForm.type]);

  // Helper function to extract related entities based on relationship type (shared)
  // Returns entities with relationship attributes (activeFrom, activeUntil, status, description) attached
  const getRelatedEntities = useCallback(
    (relationshipTypes, asSource = true, entityTypeFilter = null) => {
      if (!relationships || !entities || !isExistingEntity) return [];

      // First, find matching entities using the original logic
      const matchingEntities = entities.filter((entity) => {
        // If entityTypeFilter is specified, check the entity type
        if (entityTypeFilter && entity.type !== entityTypeFilter) {
          return false;
        }
        return relationships.some((rel) => {
          const matchesId = asSource
            ? rel.source_entity_id === editingEntity.id &&
              rel.target_entity_id === entity.id
            : rel.target_entity_id === editingEntity.id &&
              rel.source_entity_id === entity.id;
          // Make relationship type comparison case-insensitive
          const relTypeLower = (rel.type || "").toLowerCase();
          const matchesType = relationshipTypes.some(
            (type) => type.toLowerCase() === relTypeLower
          );
          return matchesId && matchesType;
        });
      });

      // Then, attach relationship attributes to each matching entity
      return matchingEntities.map((entity) => {
        // Find the relationship for this entity
        const rel = relationships.find((rel) => {
          const matchesId = asSource
            ? rel.source_entity_id === editingEntity.id &&
              rel.target_entity_id === entity.id
            : rel.target_entity_id === editingEntity.id &&
              rel.source_entity_id === entity.id;
          if (!matchesId) return false;
          const relTypeLower = (rel.type || "").toLowerCase();
          const matchesType = relationshipTypes.some(
            (type) => type.toLowerCase() === relTypeLower
          );
          return matchesType;
        });

        return {
          ...entity,
          activeFrom: rel?.active_from || null,
          activeUntil: rel?.active_until || null,
          status: rel?.status || null,
          description: rel?.description || null,
        };
      });
    },
    [entityId, entities, relationships, isExistingEntity]
  );

  // Initialize form when editingEntity changes.
  // This should run when we first open an entity AND when the parent replaces
  // editingEntity with a fresher copy from the backend (same id but new fields
  // like alias_names/custom_attributes).
  useEffect(() => {
    if (isExistingEntity && editingEntity) {
      const editingEntityType =
        editingEntity.entity_type || editingEntity.type || "";
      const signature = JSON.stringify({
        id: entityId,
        type: editingEntityType,
        subtype: editingEntity.subtype || "",
        description: editingEntity.description || "",
        status:
          editingEntity.status ||
          editingEntity.processing_status ||
          "Processed",
        alias_names: editingEntity.alias_names || "",
        custom_attributes: editingEntity.custom_attributes || {},
      });

      // Keep signature tracking for debugging/state-change avoidance,
      // but do not early-return; the edit form must always reflect the latest
      // backend entity payload.
      if (signature !== lastEditingEntitySignature) {
        setLastEditingEntitySignature(signature);
      }

      // Extract existing relationships
      const existingRelationships = {
        related_data_objects: getRelatedEntities(
          ["updates", "creates", "reads", "deletes", "transfers"],
          true,
          "data-object"
        ),
        related_business_contexts: getRelatedEntities(
          ["supports", "enables", "affects", "associated_with", "linked"],
          true,
          "business-context"
        ),
        related_business_capabilities: getRelatedEntities(
          ["supports", "improves"],
          true,
          "business-capability"
        ),
        related_it_components: getRelatedEntities(
          ["uses"],
          true,
          "technology-component"
        ),
        provided_interfaces: getRelatedEntities(
          ["provides"],
          true,
          "interface"
        ),
        consumed_interfaces: getRelatedEntities(
          ["consumes"],
          false,
          "interface"
        ), // Consumed interfaces are INCOMING relationships
        // Incoming relationships for interfaces (interface is target)
        interface_provided_by_app: getRelatedEntities(
          ["provides"],
          false,
          "application"
        ), // Which apps provide this interface?
        interface_consumed_by_app: getRelatedEntities(
          ["consumes"],
          false,
          "application"
        ), // Which apps consume this interface?
        interface_affected_by_initiative: getRelatedEntities(
          ["affects"],
          false,
          "initiative"
        ), // Which initiatives affect this interface?
        interface_affected_by_initiative_reverse: getRelatedEntities(
          ["affects", "impacts"],
          true,
          "interface"
        ), // Which interfaces are affected by this initiative?
        organization_owns_initiative_reverse: getRelatedEntities(
          ["owns", "owned by", "is owned by", "ownership", "governs"],
          false,
          "organization"
        ), // Which organizations own this initiative?
        provider_supports_initiative_reverse: getRelatedEntities(
          ["supports", "provides", "serves"],
          false,
          "provider"
        ), // Which providers support this initiative?
        organization_owns_initiative_reverse: getRelatedEntities(
          ["owns", "owned by", "is owned by", "ownership", "governs"],
          false,
          "organization"
        ), // Which organizations own this initiative?
        provider_supports_initiative_reverse: getRelatedEntities(
          ["supports", "provides", "serves"],
          false,
          "provider"
        ), // Which providers support this initiative?
        // Incoming relationships for data objects (data object is target)
        data_object_transferred_by_interface: getRelatedEntities(
          [
            "transfers",
            "accesses",
            "reads",
            "writes",
            "creates",
            "updates",
            "deletes",
          ],
          false,
          "interface"
        ), // Which interfaces transfer this data object?
        data_object_affected_by_initiative: getRelatedEntities(
          ["affects"],
          false,
          "initiative"
        ), // Which initiatives affect this data object?
        // Outgoing relationships for interfaces (interface is source, technology component is target)
        technology_component_used_by_interface: getRelatedEntities(
          ["implements", "uses"],
          true,
          "technology-component"
        ), // With which IT components is this interface implemented?
        technology_component_used_by_application: getRelatedEntities(
          ["uses", "runs on", "depends on"],
          false,
          "application"
        ), // Which applications use this technology component?
        technology_component_offered_by_provider: getRelatedEntities(
          ["provides", "offers", "supplies"],
          false,
          "provider"
        ), // Which providers offer this technology component?
        technology_component_owned_by_organization: getRelatedEntities(
          ["owns"],
          false,
          "organization"
        ), // Which organizations own this technology component?
        technology_component_affected_by_initiative: getRelatedEntities(
          ["affects"],
          false,
          "initiative"
        ), // Which initiatives affect this technology component?
        // Objective incoming relationships (objective is target)
        objective_supported_by_initiative: getRelatedEntities(
          ["supports"],
          false,
          "initiative"
        ), // Which initiatives support this objective?
        objective_supported_by_platform: getRelatedEntities(
          ["supports"],
          false,
          "platform"
        ), // Which platforms support this objective?
        // Application incoming relationships (application is target)
        initiative_affects_application_reverse: getRelatedEntities(
          ["affects"],
          false,
          "initiative"
        ), // Which initiatives affect this application?
        platform_runs_application_reverse: getRelatedEntities(
          ["runs"],
          false,
          "platform"
        ), // Which platforms run this application?
        // Business context incoming relationships (business-context is target)
        initiative_affects_business_context_reverse: getRelatedEntities(
          ["affects", "impacts"],
          false,
          "initiative"
        ), // Which initiatives affect this business context?
        application_supports_business_context_reverse: getRelatedEntities(
          ["supports", "enables"],
          false,
          "application"
        ), // Which applications support this business context?
        organization_owns_business_context_reverse: getRelatedEntities(
          ["owns"],
          false,
          "organization"
        ), // Which organizations own this business context?
        business_context_associated_with_business_capability: getRelatedEntities(
          ["associated_with", "linked", "links"],
          true,
          "business-capability"
        ), // Which business capabilities is this business context associated with?
        business_capability_linked_to_business_context_reverse:
          getRelatedEntities(
            ["linked", "associated_with", "links"],
            false,
            "business-capability"
          ), // Which business capabilities are linked to this business context?
        // Business capability incoming relationships (business-capability is target)
        initiative_improves_business_capability_reverse: getRelatedEntities(
          ["improves", "supports", "enables"],
          false,
          "initiative"
        ), // Which initiatives improve this business capability?
        objective_improves_business_capability_reverse: getRelatedEntities(
          ["improves", "supports", "enables"],
          false,
          "objective"
        ), // Which objectives are improving this business capability?
        // Platform incoming relationships (platform is target)
        initiative_affects_platform_reverse: getRelatedEntities(
          ["affects"],
          false,
          "initiative"
        ), // Which initiatives affect this platform?
        technology_component_implements_platform: getRelatedEntities(
          ["implements"],
          true,
          "platform"
        ), // Which platforms does this technology component implement?
        platform_supports_objective: getRelatedEntities(
          ["supports", "enables"],
          true,
          "objective"
        ), // Which objectives are supported by this platform?
        // Organization outgoing relationships (organization is source)
        organization_owns_objective_reverse: getRelatedEntities(
          ["owns"],
          true,
          "objective"
        ), // Which objectives are owned by this organization?
        organization_related_business_contexts: getRelatedEntities(
          ["owns", "linked", "associated_with", "supports", "affects"],
          true,
          "business-context"
        ), // Which business contexts are related to this organization?
      };

      console.log("🔍 Existing relationships loaded:", existingRelationships);

      // Debug specific to interface consumed by app
      if (
        editingEntityType === "interface" &&
        relationships &&
        relationships.length > 0
      ) {
        const consumesRels = relationships.filter(
          (rel) =>
            rel.target_entity_id === editingEntity.id &&
            (rel.type || "").toLowerCase() === "consumes"
        );
        console.log(
          "🔍 Relationships where interface is target with 'consumes' type:",
          consumesRels
        );
        console.log(
          "🔍 Applications that consume this interface:",
          existingRelationships.interface_consumed_by_app
        );
      }
      console.log(
        "🔍 useEntityForm: editingEntity.alias_names =",
        editingEntity.alias_names
      );
      setEntityForm({
        name: editingEntity.name || "",
        // Prefer entity_type when available because some payloads carry stale `type`.
        type: editingEntityType, // Will be set dynamically from API
        description: editingEntity.description || "",
        status:
          editingEntity.status ||
          editingEntity.processing_status ||
          "Processed",
        processing_status:
          editingEntity.processing_status ||
          editingEntity.status ||
          "Processed",
        custom_attributes: (() => {
          // Parse custom_attributes - handle both object and JSON string formats
          const value = editingEntity.custom_attributes;
          if (!value) return {};
          if (typeof value === "object" && !Array.isArray(value)) {
            return value;
          }
          if (typeof value === "string") {
            try {
              const parsed = JSON.parse(value);
              return typeof parsed === "object" && !Array.isArray(parsed)
                ? parsed
                : {};
            } catch {
              return {};
            }
          }
          return {};
        })(),
        alias_names: editingEntity.alias_names || "",
        programming_language: editingEntity.programming_language || "",
        database_type: editingEntity.database_type || "",
        business_capability: editingEntity.business_capability || "",
        business_unit: editingEntity.business_unit || "",
        cost_center: editingEntity.cost_center || "",
        level: editingEntity.level || "",
        deployment_environment: editingEntity.deployment_environment || "",
        user_count: editingEntity.user_count || "",
        subtype: editingEntity.subtype || "",
        // Hierarchical/sequential relationships for RelationshipManager
        // Parse these fields - they might come as JSON strings or arrays
        parent: editingEntity.parent || "",
        children: parseEntityIdArray(editingEntity.children),
        predecessors: parseEntityIdArray(editingEntity.predecessors),
        successors: parseEntityIdArray(editingEntity.successors),
        // Initialize relationship arrays by loading from relationships data
        related_organizations: [],
        related_initiatives: [],
        related_business_capabilities:
          existingRelationships.related_business_capabilities || [],
        related_applications: [],
        related_it_components:
          existingRelationships.related_it_components || [],
        related_business_actors: [],
        related_data_objects: existingRelationships.related_data_objects || [],
        related_business_contexts:
          existingRelationships.related_business_contexts || [],
        related_providers: [],
        related_goals: [],
        related_business_processes: [],
        related_value_chains: [],
        provided_interfaces: existingRelationships.provided_interfaces || [],
        consumed_interfaces: existingRelationships.consumed_interfaces || [],
        interface_provided_by_app:
          existingRelationships.interface_provided_by_app || [],
        interface_exposed_by_app: [],
        interface_consumed_by_app:
          existingRelationships.interface_consumed_by_app || [],
        interface_affected_by_initiative:
          existingRelationships.interface_affected_by_initiative || [],
        interface_affected_by_initiative_reverse:
          existingRelationships.interface_affected_by_initiative_reverse || [],
        organization_owns_initiative_reverse:
          existingRelationships.organization_owns_initiative_reverse || [],
        organization_owns_objective_reverse:
          existingRelationships.organization_owns_objective_reverse || [],
        organization_related_business_contexts:
          existingRelationships.organization_related_business_contexts || [],
        // Business context incoming relationships
        initiative_affects_business_context_reverse:
          existingRelationships.initiative_affects_business_context_reverse ||
          [],
        application_supports_business_context_reverse:
          existingRelationships.application_supports_business_context_reverse ||
          [],
        organization_owns_business_context_reverse:
          existingRelationships.organization_owns_business_context_reverse ||
          [],
        business_context_associated_with_business_capability:
          existingRelationships.business_context_associated_with_business_capability ||
          [],
        business_capability_linked_to_business_context_reverse:
          existingRelationships.business_capability_linked_to_business_context_reverse ||
          [],
        provider_supports_initiative_reverse:
          existingRelationships.provider_supports_initiative_reverse || [],
        // Data object incoming relationships
        data_object_transferred_by_interface:
          existingRelationships.data_object_transferred_by_interface || [],
        data_object_affected_by_initiative:
          existingRelationships.data_object_affected_by_initiative || [],
        // Technology component incoming relationships
        technology_component_used_by_interface:
          existingRelationships.technology_component_used_by_interface || [],
        technology_component_used_by_application:
          existingRelationships.technology_component_used_by_application || [],
        technology_component_offered_by_provider:
          existingRelationships.technology_component_offered_by_provider || [],
        it_provided_by_provider_reverse:
          existingRelationships.it_provided_by_provider_reverse ||
          existingRelationships.technology_component_offered_by_provider ||
          [],
        technology_component_owned_by_organization:
          existingRelationships.technology_component_owned_by_organization ||
          [],
        technology_component_affected_by_initiative:
          existingRelationships.technology_component_affected_by_initiative ||
          [],
        // Objective incoming relationships
        objective_supported_by_initiative:
          existingRelationships.objective_supported_by_initiative || [],
        objective_supported_by_platform:
          existingRelationships.objective_supported_by_platform || [],
        // Application incoming relationships
        initiative_affects_application_reverse:
          existingRelationships.initiative_affects_application_reverse || [],
        platform_runs_application_reverse:
          existingRelationships.platform_runs_application_reverse || [],
        // Business context incoming relationships
        initiative_affects_business_context_reverse:
          existingRelationships.initiative_affects_business_context_reverse ||
          [],
        // Business capability incoming relationships
        initiative_improves_business_capability_reverse:
          existingRelationships.initiative_improves_business_capability_reverse ||
          [],
        objective_improves_business_capability_reverse:
          existingRelationships.objective_improves_business_capability_reverse ||
          [],
        objective_improves_business_capability_reverse:
          existingRelationships.objective_improves_business_capability_reverse ||
          [],
        // Platform incoming relationships
        initiative_affects_platform_reverse:
          existingRelationships.initiative_affects_platform_reverse || [],
        technology_component_implements_platform:
          existingRelationships.technology_component_implements_platform || [],
        platform_supports_objective:
          existingRelationships.platform_supports_objective || [],
        platform_supports_objective:
          existingRelationships.platform_supports_objective || [],
        related_successors: (() => {
          const ids = parseEntityIdArray(editingEntity.successors);
          if (!ids.length) return [];
          const idSet = new Set(ids);
          return entities.filter((e) => e?.id && idSet.has(e.id));
        })(),
        // Initialize parent relationship arrays
        related_organization_parents: [],
        related_initiative_parents: [],
        related_businesscontext_parents: [],
        related_businesscapability_parents: [],
        related_applicationinterface_parents: [],
        // Initiative-specific fields removed: affected_*
      });
      console.log(
        "🔍 useEntityForm: Form initialized, entityForm.version:",
        editingEntity.version || ""
      );

      // Fetch subtypes for the entity type when editing (async, doesn't block form initialization)
      if (editingEntity.type) {
        fetchSubtypes(editingEntity.type);
      }
    }
  }, [entityId, isExistingEntity, lastEditingEntitySignature, getRelatedEntities]);

  // Update relationships when entities or relationships change (but don't reinitialize form)
  useEffect(() => {
    if (isExistingEntity && editingEntity && entities && relationships) {
      // Extract existing relationships using shared helper
      const existingRelationships = {
        related_data_objects: getRelatedEntities(
          ["updates", "creates", "reads", "deletes", "transfers"],
          true,
          "data-object"
        ),
        interface_affected_by_initiative_reverse: getRelatedEntities(
          ["affects", "impacts"],
          true,
          "interface"
        ),
        related_business_contexts: getRelatedEntities(
          ["supports", "enables", "affects", "associated_with", "linked"],
          true,
          "business-context"
        ),
        related_business_capabilities: getRelatedEntities(
          ["supports", "improves"],
          true,
          "business-capability"
        ),
        related_it_components: getRelatedEntities(
          ["uses"],
          true,
          "technology-component"
        ),
        provided_interfaces: getRelatedEntities(
          ["provides"],
          true,
          "interface"
        ),
        consumed_interfaces: getRelatedEntities(
          ["consumes"],
          false,
          "interface"
        ),
        // Incoming relationships for interfaces (interface is target)
        interface_provided_by_app: getRelatedEntities(
          ["provides"],
          false,
          "application"
        ), // Which apps provide this interface?
        interface_consumed_by_app: getRelatedEntities(
          ["consumes"],
          false,
          "application"
        ), // Which apps consume this interface?
        interface_affected_by_initiative: getRelatedEntities(
          ["affects"],
          false,
          "initiative"
        ), // Which initiatives affect this interface?
        // Incoming relationships for data objects (data object is target)
        data_object_transferred_by_interface: getRelatedEntities(
          [
            "transfers",
            "accesses",
            "reads",
            "writes",
            "creates",
            "updates",
            "deletes",
          ],
          false,
          "interface"
        ), // Which interfaces transfer this data object?
        data_object_affected_by_initiative: getRelatedEntities(
          ["affects"],
          false,
          "initiative"
        ), // Which initiatives affect this data object?
        // Outgoing relationships for interfaces (interface is source, technology component is target)
        technology_component_used_by_interface: getRelatedEntities(
          ["implements", "uses"],
          true,
          "technology-component"
        ), // With which IT components is this interface implemented?
        technology_component_used_by_application: getRelatedEntities(
          ["uses", "runs on", "depends on"],
          false,
          "application"
        ), // Which applications use this technology component?
        technology_component_offered_by_provider: getRelatedEntities(
          ["provides", "offers", "supplies"],
          false,
          "provider"
        ), // Which providers offer this technology component?
        technology_component_owned_by_organization: getRelatedEntities(
          ["owns"],
          false,
          "organization"
        ), // Which organizations own this technology component?
        technology_component_affected_by_initiative: getRelatedEntities(
          ["affects"],
          false,
          "initiative"
        ), // Which initiatives affect this technology component?
        // Objective incoming relationships (objective is target)
        objective_supported_by_initiative: getRelatedEntities(
          ["supports"],
          false,
          "initiative"
        ), // Which initiatives support this objective?
        objective_supported_by_platform: getRelatedEntities(
          ["supports"],
          false,
          "platform"
        ), // Which platforms support this objective?
        // Application incoming relationships (application is target)
        initiative_affects_application_reverse: getRelatedEntities(
          ["affects"],
          false,
          "initiative"
        ), // Which initiatives affect this application?
        platform_runs_application_reverse: getRelatedEntities(
          ["runs"],
          false,
          "platform"
        ), // Which platforms run this application?
        // Business context incoming relationships (business-context is target)
        initiative_affects_business_context_reverse: getRelatedEntities(
          ["affects", "impacts"],
          false,
          "initiative"
        ), // Which initiatives affect this business context?
        application_supports_business_context_reverse: getRelatedEntities(
          ["supports", "enables"],
          false,
          "application"
        ), // Which applications support this business context?
        organization_owns_business_context_reverse: getRelatedEntities(
          ["owns"],
          false,
          "organization"
        ), // Which organizations own this business context?
        business_context_associated_with_business_capability: getRelatedEntities(
          ["associated_with", "linked", "links"],
          true,
          "business-capability"
        ), // Which business capabilities is this business context associated with?
        business_capability_linked_to_business_context_reverse:
          getRelatedEntities(
            ["linked", "associated_with", "links"],
            false,
            "business-capability"
          ), // Which business capabilities are linked to this business context?
        // Business capability incoming relationships (business-capability is target)
        initiative_improves_business_capability_reverse: getRelatedEntities(
          ["improves", "supports", "enables"],
          false,
          "initiative"
        ), // Which initiatives improve this business capability?
        objective_improves_business_capability_reverse: getRelatedEntities(
          ["improves", "supports", "enables"],
          false,
          "objective"
        ), // Which objectives are improving this business capability?
        // Platform incoming relationships (platform is target)
        initiative_affects_platform_reverse: getRelatedEntities(
          ["affects"],
          false,
          "initiative"
        ), // Which initiatives affect this platform?
        technology_component_implements_platform: getRelatedEntities(
          ["implements"],
          true,
          "platform"
        ), // Which platforms does this technology component implement?
      };

      // Only update the relationship fields, don't reinitialize the entire form
      setEntityForm((prev) => ({
        ...prev,
        related_business_capabilities:
          existingRelationships.related_business_capabilities || [],
        related_it_components:
          existingRelationships.related_it_components || [],
        related_data_objects: existingRelationships.related_data_objects || [],
        interface_affected_by_initiative_reverse:
          existingRelationships.interface_affected_by_initiative_reverse || [],
        organization_owns_objective_reverse:
          existingRelationships.organization_owns_objective_reverse || [],
        organization_related_business_contexts:
          existingRelationships.organization_related_business_contexts || [],
        // Business context incoming relationships
        initiative_affects_business_context_reverse:
          existingRelationships.initiative_affects_business_context_reverse ||
          [],
        application_supports_business_context_reverse:
          existingRelationships.application_supports_business_context_reverse ||
          [],
        organization_owns_business_context_reverse:
          existingRelationships.organization_owns_business_context_reverse ||
          [],
        business_context_associated_with_business_capability:
          existingRelationships.business_context_associated_with_business_capability ||
          [],
        business_capability_linked_to_business_context_reverse:
          existingRelationships.business_capability_linked_to_business_context_reverse ||
          [],
        related_business_contexts:
          existingRelationships.related_business_contexts || [],
        provided_interfaces: existingRelationships.provided_interfaces || [],
        consumed_interfaces: existingRelationships.consumed_interfaces || [],
        interface_provided_by_app:
          existingRelationships.interface_provided_by_app || [],
        interface_consumed_by_app:
          existingRelationships.interface_consumed_by_app || [],
        interface_affected_by_initiative:
          existingRelationships.interface_affected_by_initiative || [],
        // Data object incoming relationships
        data_object_transferred_by_interface:
          existingRelationships.data_object_transferred_by_interface || [],
        data_object_affected_by_initiative:
          existingRelationships.data_object_affected_by_initiative || [],
        // Technology component incoming relationships
        technology_component_used_by_interface:
          existingRelationships.technology_component_used_by_interface || [],
        technology_component_used_by_application:
          existingRelationships.technology_component_used_by_application || [],
        technology_component_offered_by_provider:
          existingRelationships.technology_component_offered_by_provider || [],
        it_provided_by_provider_reverse:
          existingRelationships.it_provided_by_provider_reverse ||
          existingRelationships.technology_component_offered_by_provider ||
          [],
        technology_component_owned_by_organization:
          existingRelationships.technology_component_owned_by_organization ||
          [],
        technology_component_affected_by_initiative:
          existingRelationships.technology_component_affected_by_initiative ||
          [],
        technology_component_implements_platform:
          existingRelationships.technology_component_implements_platform || [],
        // Objective incoming relationships
        objective_supported_by_initiative:
          existingRelationships.objective_supported_by_initiative || [],
        objective_supported_by_platform:
          existingRelationships.objective_supported_by_platform || [],
        // Application incoming relationships
        initiative_affects_application_reverse:
          existingRelationships.initiative_affects_application_reverse || [],
        platform_runs_application_reverse:
          existingRelationships.platform_runs_application_reverse || [],
        // Business context incoming relationships
        initiative_affects_business_context_reverse:
          existingRelationships.initiative_affects_business_context_reverse ||
          [],
        // Business capability incoming relationships
        initiative_improves_business_capability_reverse:
          existingRelationships.initiative_improves_business_capability_reverse ||
          [],
        // Platform incoming relationships
        initiative_affects_platform_reverse:
          existingRelationships.initiative_affects_platform_reverse || [],
        related_successors: (() => {
          const ids = parseEntityIdArray(editingEntity.successors);
          if (!ids.length) return [];
          const idSet = new Set(ids);
          return entities.filter((e) => e?.id && idSet.has(e.id));
        })(),
      }));
    }
  }, [
    editingEntity?.id,
    entities,
    relationships,
    getRelatedEntities,
  ]); // Update when entities or relationships change

  // Pre-load subtypes for all entity types on mount
  useEffect(() => {
    const loadAllSubtypes = async () => {
      try {
        setLoadingAllSubtypes(true);
        console.log("🚀 Pre-loading subtypes for all entity types...");

        // Try to get from local cache first
        const cachedSubtypes = entitySubtypesCache.get();
        if (cachedSubtypes && cachedSubtypes.length > 0) {
          console.log(
            `✅ Using local cache for entity subtypes (${cachedSubtypes.length} subtypes)`
          );

          // Group subtypes by entity_type
          const cache = {};
          cachedSubtypes.forEach((subtype) => {
            const entityType = subtype.entity_type;
            if (!cache[entityType]) {
              cache[entityType] = [];
            }
            cache[entityType].push(subtype);
          });

          setSubtypesCache(cache);
          console.log(
            `✅ Pre-loaded subtypes for ${Object.keys(cache).length} entity types from local cache`
          );
          return;
        }

        // Fetch ALL subtypes in a single API call (backend cache has them all)
        try {
          const response = await apiFetch("/api/entity-subtypes");
          const data = await response.json();
          const allSubtypes = data?.subtypes || [];

          console.log(
            `📋 Fetched ${allSubtypes.length} total subtypes from backend cache`
          );

          // Save to local cache
          entitySubtypesCache.set(allSubtypes);

          // Group subtypes by entity_type
          const cache = {};
          allSubtypes.forEach((subtype) => {
            const entityType = subtype.entity_type;
            if (!cache[entityType]) {
              cache[entityType] = [];
            }
            cache[entityType].push(subtype);
          });

          setSubtypesCache(cache);
          console.log(
            `✅ Pre-loaded subtypes for ${Object.keys(cache).length} entity types`
          );
        } catch (error) {
          console.error("❌ Error fetching all subtypes from API:", error);
        }
      } catch (error) {
        console.error("❌ Error pre-loading subtypes:", error);
      } finally {
        setLoadingAllSubtypes(false);
      }
    };

    loadAllSubtypes();
  }, []); // Run only once on mount

  // Function to fetch subtypes for the current entity type (now reads from cache)
  const fetchSubtypes = async (entityType) => {
    if (!entityType) return;

    console.log("🔍 Getting subtypes for entity type:", entityType);
    setLoadingSubtypes(true);

    // Read from pre-loaded cache
    const cachedSubtypes = subtypesCache[entityType];
    if (cachedSubtypes !== undefined) {
      console.log("✅ Using cached subtypes:", cachedSubtypes);
      setAvailableSubtypes(Array.isArray(cachedSubtypes) ? cachedSubtypes : []);
      setLoadingSubtypes(false);
      return;
    }

    // Fallback: fetch if not in cache (shouldn't happen if preload works)
    console.warn("⚠️ Subtypes not in cache, fetching...");
    try {
      const response = await getEntitySubtypes(entityType);
      const subtypes = response?.subtypes || response || [];
      setAvailableSubtypes(Array.isArray(subtypes) ? subtypes : []);
    } catch (error) {
      console.error("❌ Error fetching subtypes:", error);
      setAvailableSubtypes([]);
    } finally {
      setLoadingSubtypes(false);
    }
  };

  // Fetch subtypes when entity type changes
  useEffect(() => {
    if (entityForm.type) {
      fetchSubtypes(entityForm.type);
      // Only clear subtype when entity type changes for NEW entities (not when editing existing ones)
      if (!isExistingEntity) {
        setEntityForm((prev) => ({
          ...prev,
          subtype: "",
        }));
      }
    }
  }, [entityForm.type, isExistingEntity, subtypesCache]); // Add subtypesCache to dependencies

  return {
    entityForm,
    setEntityForm,
    availableSubtypes,
    loadingSubtypes,
    loadingAllSubtypes,
    removedItems,
    setRemovedItems,
    fetchSubtypes,
  };
};
